/**
 * `wpp::form.select.searchable`
 *
 * Livewire is assumed here — the whole point of the component is a server-side
 * search — so `$wire` is not guarded. The record list is: the markup omits the
 * suggestions entirely when the field is in input mode with nothing to suggest,
 * and every arrow key then went through `$refs.records.querySelectorAll` on
 * `undefined`.
 */
export default (valueProperty, searchProperty, displayAsInput = false, allowNewRecord = false) => ({
    open: false,
    inputValue: '',
    selectedName: '',
    highlightedIndex: -1,
    displayAsInput,
    allowNewRecord,
    init() {
        this.inputValue = this.$wire.get(searchProperty) || '';
        this.selectedName = this.$wire.get(searchProperty) || '';

        const currentValue = this.$wire.get(valueProperty);
        if (currentValue) {
            this.syncDisplayFromValue(currentValue);
        }
    },
    /** The suggestion buttons, or an empty list when there are no suggestions. */
    records() {
        return Array.from(this.$refs.records?.querySelectorAll('button') ?? []);
    },

    /**
     * What a row is called, for the trigger to display.
     *
     * `data-name` rather than the button's text, because a row may draw more
     * than its name — a second line under it, an icon beside it — and the field
     * shows one line. Reading `textContent` put every line of a two-line row
     * into the trigger, run together and with the whitespace between them.
     *
     * The fallback is for a row built entirely from the slot, which may not have
     * set a label. It is the old behaviour, kept for exactly that case.
     */
    nameOf(record) {
        const name = record.getAttribute('data-name');

        return name !== null && name !== '' ? name : record.textContent.trim();
    },

    syncDisplayFromValue(id) {
        const records = this.records();

        records.forEach((record, index) => {
            if (record.getAttribute('data-id') == id) {
                this.highlightedIndex = index;
                this.selectedName = this.nameOf(record);
                if (this.displayAsInput) {
                    this.inputValue = this.selectedName;
                }
            }
        });
    },
    toggleDropdown() {
        if (this.open) {
            this.handleClosing();
        } else {
            this.open = true;
            this.$nextTick(() => {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                }
            });
        }
    },
    selectRecord(id, shouldClose = true) {
        this.syncDisplayFromValue(id);
        this.$wire.set(valueProperty, id);

        if (shouldClose) {
            this.open = false;
            this.inputValue = '';
        }
    },
    handleInput() {
        this.open = true;
    },
    handleClosing() {
        if (this.displayAsInput) {
            if (this.allowNewRecord && this.inputValue && this.inputValue !== this.selectedName) {
                this.$wire.set(valueProperty, this.inputValue);
            } else if (!this.allowNewRecord && this.inputValue !== this.selectedName) {
                this.inputValue = this.selectedName;
            }
        } else {
            this.inputValue = '';
        }

        this.open = false;
        this.highlightedIndex = -1;
    },
    highlightNextRecord() {
        this.open = true;

        const records = this.records();
        if (this.highlightedIndex < records.length - 1) {
            this.highlightedIndex++;
            this.updateHighlight(records);
        }
    },
    highlightPreviousRecord() {
        this.open = true;
        const records = this.records();
        if (this.highlightedIndex > 0) {
            this.highlightedIndex--;
            this.updateHighlight(records);
        }
    },
    updateHighlight(records) {
        records.forEach((record, index) => {
            if (index === this.highlightedIndex) {
                record.classList.add('bg-wpp-gray-100');
                record.scrollIntoView({ block: 'nearest' });
            } else {
                record.classList.remove('bg-wpp-gray-100');
            }
        });
    },
    selectHighlightedRecord() {
        const records = this.records();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < records.length) {
            const record = records[this.highlightedIndex];
            this.selectRecord(record.getAttribute('data-id'));
        }
    },
});
